import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Calendar as CalendarIcon, Clock, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import Header from '../Header';
import {
  SPORT_TYPES,
  POOL_LANES_OPTIONS,
  DEFAULT_POOL_LANES,
  SWIM_PRESETS,
  DEFAULT_SWIM_PRESET,
  POOL_UNITS,
  DEFAULT_POOL_UNIT,
  getSportLabel,
  getSwimPresetLabel,
  shouldShowPoolLanes,
} from '../../utils/sportConfig';

// Get unique venues from past meets
const getPastVenues = () => {
  try {
    const storedMeets = localStorage.getItem('foresight_meets');
    if (storedMeets) {
      const meets = JSON.parse(storedMeets);
      const venues = meets
        .map(m => m.venue)
        .filter(v => v && v.trim() !== '');
      // Return unique venues, most recent first
      return [...new Set(venues)];
    }
  } catch (e) {
    console.error('Error loading past venues:', e);
  }
  return [];
};

export default function MeetDetails({ data, onUpdate, onNext, onBack, isEditing = false }) {
  const [date, setDate] = useState(data.date ? new Date(data.date) : undefined);
  const [showVenueSuggestions, setShowVenueSuggestions] = useState(false);
  const [pastVenues, setPastVenues] = useState([]);
  const venueInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Load past venues on mount
  useEffect(() => {
    setPastVenues(getPastVenues());
  }, []);

  // Update date when data.date changes (e.g., when editing and data loads async)
  useEffect(() => {
    if (data.date) {
      setDate(new Date(data.date));
    }
  }, [data.date]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        venueInputRef.current &&
        !venueInputRef.current.contains(event.target)
      ) {
        setShowVenueSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter venues based on current input
  const filteredVenues = pastVenues.filter(venue =>
    venue.toLowerCase().includes((data.venue || '').toLowerCase())
  );

  const handleVenueSelect = (venue) => {
    onUpdate({ venue });
    setShowVenueSuggestions(false);
  };

  const handleContinue = () => {
    if (!data.name || !data.venue || !date || !data.startTime || !data.level) {
      toast.error('Please fill in all required fields');
      return;
    }
    // Swim requires a pool-lanes pick — heat sizing depends on it.
    if (data.sport_type === 'swim' && !data.pool_lanes) {
      toast.error('Please pick the number of pool lanes');
      return;
    }
    onUpdate({ date: date.toISOString() });
    onNext();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBack={onBack} title={isEditing ? "Edit Meet Details" : "Meet Details"} />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Template Notice - only show when creating from template, not when editing */}
        {data.isFromTemplate && !isEditing && (
          <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Using template from:</strong> {data.templateSourceName}
              <br />
              <span className="text-blue-600">Events and settings copied. Please enter new date, time, and venue below.</span>
            </p>
          </Card>
        )}
        
        <Card className="p-6">
          <div className="space-y-6">
            {/* Meet Name */}
            <div className="space-y-2">
              <Label htmlFor="meet-name">Meet Name *</Label>
              <Input 
                id="meet-name"
                placeholder="e.g., Spring Invitational 2025"
                value={data.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
              />
            </div>

            {/* Venue */}
            <div className="space-y-2">
              <Label htmlFor="venue">Venue *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                <Input 
                  ref={venueInputRef}
                  id="venue"
                  placeholder="e.g., Central High School Track"
                  value={data.venue}
                  onChange={(e) => onUpdate({ venue: e.target.value })}
                  onFocus={() => pastVenues.length > 0 && setShowVenueSuggestions(true)}
                  className="pl-10 pr-10"
                  autoComplete="off"
                />
                {pastVenues.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowVenueSuggestions(!showVenueSuggestions)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <ChevronDown className={cn("w-4 h-4 transition-transform", showVenueSuggestions && "rotate-180")} />
                  </button>
                )}
                
                {/* Venue Suggestions Dropdown */}
                {showVenueSuggestions && filteredVenues.length > 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto"
                  >
                    <div className="p-1">
                      <p className="px-2 py-1 text-xs text-muted-foreground font-medium">Recent Venues</p>
                      {filteredVenues.map((venue, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleVenueSelect(venue)}
                          className="w-full text-left px-2 py-2 text-sm hover:bg-muted rounded-sm flex items-center gap-2"
                        >
                          <MapPin className="w-3 h-3 text-muted-foreground" />
                          {venue}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the name of the track, stadium, or venue
              </p>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !date && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="start-time">Start Time *</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    id="start-time"
                    type="time"
                    className="pl-10"
                    value={data.startTime}
                    onChange={(e) => onUpdate({ startTime: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Sport — multi-sport expansion (Feb 2026). Defaults to
                Track; selecting Swim reveals a Pool Lanes picker so
                heat counts can be sized to the venue. Selecting XC or
                Swim also hides field-event UI downstream. */}
            <div className="space-y-2">
              <Label>Sport *</Label>
              <div className="grid grid-cols-3 gap-3" data-testid="sport-picker">
                {SPORT_TYPES.map((sport) => (
                  <Button
                    key={sport}
                    type="button"
                    variant={data.sport_type === sport ? 'default' : 'outline'}
                    onClick={() => {
                      const update = { sport_type: sport };
                      // Switching away from swim wipes the pool_lanes
                      // value so it doesn't leak into the saved meet.
                      // Switching INTO swim seeds the default lane count
                      // so the spectator-side heat sizing has a value.
                      if (sport === 'swim') {
                        update.pool_lanes = data.pool_lanes || DEFAULT_POOL_LANES;
                        update.swim_preset = data.swim_preset || DEFAULT_SWIM_PRESET;
                        update.pool_unit = data.pool_unit || DEFAULT_POOL_UNIT;
                      } else {
                        update.pool_lanes = null;
                        update.swim_preset = null;
                        update.pool_unit = null;
                      }
                      // XC is exclusively an outdoor sport — hardcode
                      // `type` so the spectator schedule + backend
                      // validation still see a populated value even
                      // though the Indoor/Outdoor toggle is hidden
                      // for the XC flow.
                      if (sport === 'xc') {
                        update.type = 'outdoor';
                      }
                      // Changing sport invalidates the previously-built
                      // events list (track sprints don't belong in a swim
                      // meet). Wipe events so ManualEventEntry rebuilds
                      // from the sport-specific default block.
                      update.events = [];
                      onUpdate(update);
                    }}
                    className="h-auto py-3 capitalize"
                    data-testid={`sport-option-${sport}`}
                  >
                    {getSportLabel(sport)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Pool Lanes — only rendered for swim. Used to size heat
                generation in downstream views. */}
            {shouldShowPoolLanes(data.sport_type) && (
              <>
                <div className="space-y-2" data-testid="swim-preset-section">
                  <Label>Swim Preset *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {SWIM_PRESETS.map((preset) => (
                      <Button
                        key={preset}
                        type="button"
                        variant={(data.swim_preset || DEFAULT_SWIM_PRESET) === preset ? 'default' : 'outline'}
                        onClick={() => onUpdate({ swim_preset: preset, events: [] })}
                        className="h-auto py-3 text-sm"
                        data-testid={`swim-preset-${preset}`}
                      >
                        {getSwimPresetLabel(preset)}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Standard High School ≈ 24 events. Summer Swim Club
                    seeds ~30 age-group events with generous youth
                    pacing — use &quot;Add Custom Event&quot; in the next
                    step to bulk-add the long tail.
                  </p>
                </div>

                <div className="space-y-2" data-testid="pool-unit-section">
                  <Label>Pool Measurement *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {POOL_UNITS.map((unit) => (
                      <Button
                        key={unit}
                        type="button"
                        variant={(data.pool_unit || DEFAULT_POOL_UNIT) === unit ? 'default' : 'outline'}
                        // Changing the unit invalidates the cached event
                        // names (which bake the 'y' or 'm' suffix in),
                        // so we wipe `events` to force ManualEventEntry
                        // to regenerate from the preset with the new
                        // unit. The NFHS 500-free / 400-free swap also
                        // only takes effect on regeneration.
                        onClick={() => onUpdate({ pool_unit: unit, events: [] })}
                        className="h-auto py-3 capitalize text-sm"
                        data-testid={`pool-unit-${unit}`}
                      >
                        {unit === 'yards' ? 'Yards (SCY)' : 'Meters'}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Yards (SCY) is the U.S. high-school / NCAA standard;
                    Meters is short-course-meters &amp; international.
                    Switching units regenerates event names and swaps
                    the 500 Free for a 400 Free per NFHS Rule 3.
                  </p>
                </div>

                <div className="space-y-2" data-testid="pool-lanes-section">
                  <Label>Pool Lanes *</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {POOL_LANES_OPTIONS.map((n) => (
                      <Button
                        key={n}
                        type="button"
                        variant={data.pool_lanes === n ? 'default' : 'outline'}
                        onClick={() => onUpdate({ pool_lanes: n })}
                        className="h-auto py-3"
                        data-testid={`pool-lanes-${n}`}
                      >
                        {n} Lanes
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Heats are sized to fit the pool — pick the number of
                    lanes at your venue.
                  </p>
                </div>
              </>
            )}

            {/* Meet Type (Indoor / Outdoor). Hidden for XC — cross
                country is exclusively outdoor, and `type` is force-
                set to 'outdoor' in the sport-picker onClick so the
                spectator schedule + backend validation still see a
                populated value. Track keeps the toggle (Indoor track
                is a full standard season). Swim renders the toggle
                today; the user has not flagged it for hiding. */}
            {data.sport_type !== 'xc' && (
              <div className="space-y-2" data-testid="meet-type-toggle">
                <Label>Meet Type *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={data.type === 'outdoor' ? 'default' : 'outline'}
                    onClick={() => onUpdate({ type: 'outdoor' })}
                    className="h-auto py-3"
                    data-testid="meet-type-outdoor"
                  >
                    Outdoor
                  </Button>
                  <Button
                    type="button"
                    variant={data.type === 'indoor' ? 'default' : 'outline'}
                    onClick={() => onUpdate({ type: 'indoor' })}
                    className="h-auto py-3"
                    data-testid="meet-type-indoor"
                  >
                    Indoor
                  </Button>
                </div>
              </div>
            )}

            {/* Level */}
            <div className="space-y-2">
              <Label>Level *</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={data.level === 'middle-school' ? 'default' : 'outline'}
                  onClick={() => onUpdate({ level: 'middle-school' })}
                  className="h-auto py-3"
                >
                  Middle/Junior
                </Button>
                <Button
                  type="button"
                  variant={data.level === 'high-school' ? 'default' : 'outline'}
                  onClick={() => onUpdate({ level: 'high-school' })}
                  className="h-auto py-3"
                >
                  High School
                </Button>
                <Button
                  type="button"
                  variant={data.level === 'college' ? 'default' : 'outline'}
                  onClick={() => onUpdate({ level: 'college' })}
                  className="h-auto py-3 hidden"
                >
                  College
                </Button>
                <Button
                  type="button"
                  variant={data.level === 'professional' ? 'default' : 'outline'}
                  onClick={() => onUpdate({ level: 'professional' })}
                  className="h-auto py-3 hidden"
                >
                  Professional
                </Button>
              </div>
              <p
                className="text-xs text-muted-foreground mt-2 leading-snug"
                data-testid="level-helper-text"
              >
                {data.sport_type === 'xc'
                  ? 'Pick a level to seed default divisions (Varsity, JV, etc.). You can rename or add divisions in the next step.'
                  : data.sport_type === 'swim'
                  ? 'Pick a level to seed common swim events. You can add or remove events in the next step.'
                  : 'Need separate qualifying rounds? You can add Prelims and Finals variants for sprints & hurdles in the next step from the Add Event picker.'}
              </p>
            </div>
          </div>
        </Card>

        {/* Continue Button */}
        <div className="flex justify-end mt-6">
          <Button size="lg" onClick={handleContinue}>
            {isEditing ? "Continue to Events" : "Continue to Events"}
          </Button>
        </div>
      </main>
    </div>
  );
}
